import React, { useState, useEffect } from 'react';
import { getCssVar } from '../contexts/ThemeContext';
import { trackMiniAppEvent, trackMiniAppTimeSpent } from '../utils/analytics';

export default function TicTacToeApp() {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [xIsNext, setXIsNext] = useState(true);
  const [gameMode, setGameMode] = useState('single'); // 'single' or 'multiplayer'
  const [startTime, setStartTime] = useState(null);

  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6] // diagonals
    ];

    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  // AI logic to find the best move
  const findBestMove = (squares) => {
    // First, try to win
    for (let i = 0; i < 9; i++) {
      if (!squares[i]) {
        const testBoard = squares.slice();
        testBoard[i] = 'O';
        if (calculateWinner(testBoard) === 'O') {
          return i;
        }
      }
    }

    // Second, block player from winning
    for (let i = 0; i < 9; i++) {
      if (!squares[i]) {
        const testBoard = squares.slice();
        testBoard[i] = 'X';
        if (calculateWinner(testBoard) === 'X') {
          return i;
        }
      }
    }

    // Third, take center if available
    if (!squares[4]) {
      return 4;
    }

    // Fourth, take corners if available
    const corners = [0, 2, 6, 8];
    const availableCorners = corners.filter(i => !squares[i]);
    if (availableCorners.length > 0) {
      return availableCorners[Math.floor(Math.random() * availableCorners.length)];
    }

    // Finally, take any available side
    const sides = [1, 3, 5, 7];
    const availableSides = sides.filter(i => !squares[i]);
    if (availableSides.length > 0) {
      return availableSides[Math.floor(Math.random() * availableSides.length)];
    }

    return null;
  };

  // Computer makes a move
  const computerMove = () => {
    const bestMove = findBestMove(board);
    if (bestMove !== null) {
      const newBoard = board.slice();
      newBoard[bestMove] = 'O';
      setBoard(newBoard);
      setXIsNext(true);
    }
  };

  // useEffect to trigger computer move after board updates
  useEffect(() => {
    if (
      gameMode === 'single' &&
      !xIsNext &&
      !calculateWinner(board) &&
      board.some(cell => cell === null)
    ) {
      const timer = setTimeout(() => {
        computerMove();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [board, xIsNext, gameMode]);

  // Track game outcomes
  useEffect(() => {
    const winner = calculateWinner(board);
    const isDraw = !winner && board.every(square => square !== null);
    
    if (winner) {
      trackMiniAppEvent.tictactoe.gameWon(winner);
      // Track time spent when game ends
      if (startTime) {
        trackMiniAppTimeSpent('tictactoe', startTime);
        setStartTime(null);
      }
    } else if (isDraw) {
      trackMiniAppEvent.tictactoe.gameDraw();
      // Track time spent when game ends
      if (startTime) {
        trackMiniAppTimeSpent('tictactoe', startTime);
        setStartTime(null);
      }
    }
  }, [board, startTime]);

  // Handle player move
  const handleClick = (i) => {
    if (calculateWinner(board) || board[i] || (gameMode === 'single' && !xIsNext)) {
      return;
    }
    
    // Track move made
    const player = gameMode === 'single' ? 'X' : (xIsNext ? 'X' : 'O');
    trackMiniAppEvent.tictactoe.moveMade(i, player);
    
    const newBoard = board.slice();
    if (gameMode === 'single') {
      newBoard[i] = 'X';
      setBoard(newBoard);
      setXIsNext(false);
    } else {
      newBoard[i] = xIsNext ? 'X' : 'O';
      setBoard(newBoard);
      setXIsNext(!xIsNext);
    }
  };

  // Reset game
  const resetGame = () => {
    trackMiniAppEvent.tictactoe.newGameClicked();
    
    // Track time spent if game was in progress
    if (startTime && board.some(cell => cell !== null)) {
      trackMiniAppTimeSpent('tictactoe', startTime);
    }
    
    setBoard(Array(9).fill(null));
    setXIsNext(true);
    setStartTime(Date.now());
  };

  // Toggle game mode
  const toggleGameMode = () => {
    const newMode = gameMode === 'single' ? 'multiplayer' : 'single';
    trackMiniAppEvent.tictactoe.modeChanged(newMode);
    
    // Track time spent if game was in progress
    if (startTime && board.some(cell => cell !== null)) {
      trackMiniAppTimeSpent('tictactoe', startTime);
    }
    
    setGameMode(newMode);
    resetGame();
  };

  const winner = calculateWinner(board);
  const isDraw = !winner && board.every(square => square !== null);
  const status = winner 
    ? `Winner: ${winner}!` 
    : isDraw 
    ? "It's a draw!" 
    : gameMode === 'single'
    ? `Next player: ${xIsNext ? 'You (X)' : 'Computer (O)'}`
    : `Next player: ${xIsNext ? 'X' : 'O'}`;

  const renderSquare = (i) => {
    const isDisabled = gameMode === 'single' && !xIsNext;
    const squareClass = `square tictactoe-square ${board[i] === 'X' ? 'x' : board[i] === 'O' ? 'o' : ''}`;
    // Classic color and style for X
    const getSquareColor = () => {
      if (board[i] === 'X') return 'var(--color-primary)';
      if (board[i] === 'O') return '#4ecdc4';
      return 'var(--color-text)';
    };
    return (
      <button
        className={squareClass}
        onClick={() => handleClick(i)}
        disabled={isDisabled}
        style={{
          width: '60px',
          height: '60px',
          border: '2px solid var(--color-text)',
          background: 'var(--color-bg-card)',
          fontSize: '32px',
          fontWeight: 'bold',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          color: getSquareColor(),
          opacity: isDisabled ? 0.7 : 1
        }}
      >
        {board[i]}
      </button>
    );
  };

  return (
    <div style={{
      padding: '20px',
      background: 'var(--color-bg-card)',
      borderRadius: '12px',
      boxShadow: '0 4px 12px var(--color-shadow)',
      margin: '10px 0',
      textAlign: 'center'
    }}>
      <style>
        {`
          .tictactoe-square {
            color: inherit !important;
          }
          .tictactoe-square.x {
            color: #ff6b6b !important;
          }
          .tictactoe-square.o {
            color: #4ecdc4 !important;
          }
        `}
      </style>
      <h3 style={{ margin: '0 0 16px 0', color: 'var(--color-text)' }}>⭕ Tic Tac Toe</h3>
      
      {/* Game Mode Toggle */}
      <div style={{ 
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        alignItems: 'center'
      }}>
        <span style={{ 
          fontSize: '14px', 
          color: 'var(--color-text)',
          opacity: 0.8
        }}>
          Mode:
        </span>
        <button
          onClick={toggleGameMode}
          style={{
            padding: '6px 12px',
            backgroundColor: gameMode === 'single' ? 'var(--color-primary)' : 'var(--color-bg)',
            color: gameMode === 'single' ? 'var(--color-bg-card)' : 'var(--color-text)',
            border: '1px solid var(--color-border)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {gameMode === 'single' ? 'Single Player' : 'Multiplayer'}
        </button>
      </div>
      
      <div style={{ 
        marginBottom: '16px',
        fontSize: '18px',
        fontWeight: 'bold',
        color: winner ? 'var(--color-primary)' : isDraw ? 'var(--color-accent)' : 'var(--color-text)'
      }}>
        {status}
      </div>

      {gameMode === 'single' && (
        <div style={{ 
          marginBottom: '16px',
          fontSize: '14px',
          color: 'var(--color-text)',
          opacity: 0.7
        }}>
          You play as X, Computer plays as O
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '2px',
        width: 'fit-content',
        margin: '0 auto 16px auto',
        background: 'var(--color-text)'
      }}>
        {renderSquare(0)}
        {renderSquare(1)}
        {renderSquare(2)}
        {renderSquare(3)}
        {renderSquare(4)}
        {renderSquare(5)}
        {renderSquare(6)}
        {renderSquare(7)}
        {renderSquare(8)}
      </div>

      <button
        onClick={resetGame}
        style={{
          padding: '8px 16px',
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-bg-card)',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px'
        }}
      >
        New Game
      </button>
    </div>
  );
} 